import { useEffect } from "react";
import { flushSync } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { loginWithBasicAuth } from "@/api/auth";
import { fetchAccountGroups } from "@/api/account";
import {
  setAuthToken,
  setRefreshToken,
  setOnUnauthorized,
  setOnTokenRefreshed,
  refreshAccessToken,
} from "@/api/client";
import type {
  Group,
  LoginCredentials,
  LoginError,
  LoginResponse,
  User,
} from "@/api/types";
import { AxiosError } from "axios";
import { appConfig } from "@/config/app.config";
import { useAuthStore } from "@/store/auth-store";

const NO_GROUPS_ERROR = "No groups available for this user";

function pickActiveGroupId(groups: Group[], previous?: number): number {
  if (previous !== undefined && groups.some((g) => g.id === previous)) {
    return previous;
  }
  return Math.min(...groups.map((g) => g.id));
}

function clearAuth(): void {
  setAuthToken(null);
  setRefreshToken(null);
  useAuthStore.getState().clearUser();
}

function setActiveGroupId(groupId: number): void {
  const current = useAuthStore.getState().user;
  if (!current) return;
  if (current.activeGroupId === groupId) return;
  useAuthStore.getState().setUser({ ...current, activeGroupId: groupId });
}

async function buildUserFromTokens(
  loginResponse: LoginResponse,
  previousActiveGroupId?: number,
): Promise<User> {
  const groups = await fetchAccountGroups();
  if (groups.length === 0) {
    throw new Error(NO_GROUPS_ERROR);
  }
  return {
    ...loginResponse,
    groups,
    activeGroupId: pickActiveGroupId(groups, previousActiveGroupId),
  };
}

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // Register module-level callbacks so the axios interceptor can trigger
  // React state updates (logout or user refresh) from outside React.
  useEffect(() => {
    setOnUnauthorized(clearAuth);

    setOnTokenRefreshed((refreshed) => {
      // The interceptor has already retried the original request — we update
      // tokens optimistically (so cross-tab listeners see fresh tokens) and
      // re-fetch groups in the background. The user keeps their previous
      // groups/activeGroupId in the interim.
      const current = useAuthStore.getState().user;
      if (!current) return;
      setUser({ ...current, ...refreshed });

      void buildUserFromTokens(refreshed, current.activeGroupId)
        .then((next) => {
          // Bail if the user logged out while groups were in flight.
          if (!useAuthStore.getState().user) return;
          setUser(next);
        })
        .catch(() => {
          // Same bail rule for the failure path: logging out, then a stale
          // groups call rejecting, must not re-trigger sign-out side effects.
          if (!useAuthStore.getState().user) return;
          clearAuth();
        });
    });
  }, [setUser]);

  // Proactively refresh the token before it expires to avoid 401 interruptions
  useEffect(() => {
    if (!user) return;

    if (!user.expiry) return; // reloaded session — no expiry in memory, reactive 401 interceptor handles refresh
    const msUntilExpiry = user.expiry - Date.now();
    const refreshIn = msUntilExpiry - appConfig.auth.accessTokenExpiryBuffer;

    if (refreshIn <= 0) {
      // Already within the margin — the response interceptor will handle it
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const refreshed = await refreshAccessToken(user.refreshToken);
        setAuthToken(refreshed.accessToken);
        setRefreshToken(refreshed.refreshToken);
        setUser(await buildUserFromTokens(refreshed, user.activeGroupId));
      } catch {
        clearAuth();
      }
    }, refreshIn);

    return () => clearTimeout(timer);
  }, [user, setUser]);

  const loginMutation = useMutation<
    User,
    AxiosError<LoginError> | Error,
    LoginCredentials
  >({
    mutationFn: async (credentials) => {
      const loginResponse = await loginWithBasicAuth(credentials);
      // Set tokens now so the bearer interceptor authenticates the next call.
      setAuthToken(loginResponse.accessToken);
      setRefreshToken(loginResponse.refreshToken);

      try {
        return await buildUserFromTokens(loginResponse);
      } catch (err) {
        setAuthToken(null);
        setRefreshToken(null);
        throw err;
      }
    },
    onSuccess: (data) => {
      // flushSync ensures the state update propagates through the component
      // tree synchronously, so that navigating after loginAsync resolves
      // will find isAuthenticated=true in the destination route.
      flushSync(() => {
        setUser(data);
      });
    },
  });

  return {
    user,
    isAuthenticated: user !== null,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: clearAuth,
    setActiveGroupId,
  };
}
